import React, { useEffect, useMemo, useState } from 'react';
import { ExternalLink, MessageCircle } from 'lucide-react';
import ModalShell from '../../shared/ModalShell';
import Button from '../../shared/Button';
import Input from '../../shared/Input';
import CustomSelect from '../../shared/CustomSelect';
import { useWorkspaceWhatsappDraft } from '../../../services/vendorWorkspaceService';
import { useVendorIntegrations } from '../../../services/vendorIntegrationsService';
import { useToastContext } from '../../../contexts/ToastContext';
import { Link } from 'react-router-dom';

const FALLBACK_TEMPLATES = [
  { value: 'follow_up', label: 'Follow-up' },
  { value: 'meeting', label: 'Meeting confirm' },
  { value: 'payment', label: 'Payment reminder' },
  { value: 'event', label: 'Event check-in' },
];

/**
 * Human-confirmed WhatsApp draft — opens wa.me; never auto-sends.
 * Templates come from Integrations (Pro).
 */
const WhatsAppDraftModal = ({ isOpen, onClose, item, canWhatsapp = false }) => {
  const { success, error: showError } = useToastContext();
  const [template, setTemplate] = useState('follow_up');
  const [phone, setPhone] = useState('');
  const [draft, setDraft] = useState(null);

  const { data: integRes } = useVendorIntegrations({ enabled: isOpen && canWhatsapp });
  const templates = integRes?.data?.integrations?.whatsapp?.templates || [];
  const waConnected = integRes?.data?.integrations?.whatsapp?.status === 'configured';
  const fromPhone = integRes?.data?.integrations?.whatsapp?.phone || '';

  const templateOptions = useMemo(() => {
    if (!templates.length) return FALLBACK_TEMPLATES;
    return templates.map((t) => ({
      value: t.id,
      label: t.name || t.id,
    }));
  }, [templates]);

  useEffect(() => {
    if (!isOpen) {
      setDraft(null);
      setPhone('');
      setTemplate('follow_up');
      return;
    }
    if (templateOptions.length && !templateOptions.some((o) => o.value === template)) {
      setTemplate(templateOptions[0].value);
    }
  }, [isOpen, templateOptions, template]);

  const mutation = useWorkspaceWhatsappDraft({
    onSuccess: (res) => {
      setDraft(res?.data || null);
      if (res?.data?.to_phone) setPhone(res.data.to_phone);
      success(null, res?.message || 'Draft ready.');
    },
    onError: (err) =>
      showError(
        err?.response?.data?.message || 'Could not build draft',
        err?.response?.data?.upgrade_required ? 'WhatsApp requires Pro.' : undefined
      ),
  });

  const buildDraft = () => {
    if (!item?.id) return;
    mutation.mutate({
      id: item.id,
      template,
      phone: phone.trim() || undefined,
    });
  };

  return (
    <ModalShell
      isOpen={isOpen}
      onClose={mutation.isPending ? undefined : onClose}
      labelledBy="wa-draft-title"
      panelClassName="max-w-lg"
    >
      <div className="w-full max-w-lg overflow-hidden rounded-md border border-border bg-surface shadow-sm">
        <div className="border-b border-border px-4 py-3 sm:px-5">
          <div className="flex items-center gap-2">
            <MessageCircle className="h-4 w-4 text-ink-tertiary" />
            <h2 id="wa-draft-title" className="text-base font-semibold text-ink">
              WhatsApp draft
            </h2>
          </div>
          <p className="mt-1 text-xs text-ink-secondary">
            Review the message, then open WhatsApp yourself. Nothing is sent automatically.
          </p>
        </div>

        <div className="space-y-3 px-4 py-4 sm:px-5">
          {!canWhatsapp ? (
            <p className="rounded-md border border-dashed border-border bg-surface-muted/40 px-3 py-2 text-sm text-ink-secondary">
              WhatsApp drafts are on Pro.{' '}
              <Link
                to="/vendor/subscription"
                className="font-medium text-ink underline-offset-2 hover:underline"
              >
                Upgrade
              </Link>{' '}
              or connect a number on{' '}
              <Link
                to="/vendor/integrations"
                className="font-medium text-ink underline-offset-2 hover:underline"
              >
                Integrations
              </Link>
              .
            </p>
          ) : (
            <>
              {!waConnected ? (
                <p className="rounded-md border border-dashed border-border bg-surface-muted/40 px-3 py-2 text-xs text-ink-secondary">
                  Connect your business number on{' '}
                  <Link
                    to="/vendor/integrations"
                    className="font-medium text-ink underline-offset-2 hover:underline"
                  >
                    Integrations
                  </Link>{' '}
                  to show as Connected. You can still draft and open WhatsApp.
                </p>
              ) : (
                <p className="text-xs text-ink-tertiary">
                  From: <span className="font-medium text-ink">{fromPhone}</span>
                </p>
              )}
              <p className="text-xs text-ink-tertiary">
                Card: <span className="font-medium text-ink">{item?.title}</span>
              </p>
              <CustomSelect
                label="Template"
                options={templateOptions}
                value={template}
                onChange={setTemplate}
              />
              <Input
                label="Client phone (E.164 or local)"
                value={phone}
                onChange={(e) => setPhone(e.target.value)}
                placeholder="+15551234567"
              />
              {draft?.body ? (
                <div className="rounded-md border border-border bg-surface-muted/50 p-3">
                  <p className="mb-1 text-[10px] font-semibold uppercase tracking-wide text-ink-tertiary">
                    Message
                    {draft.template_name ? ` · ${draft.template_name}` : ''}
                  </p>
                  <p className="whitespace-pre-wrap text-sm text-ink">{draft.body}</p>
                </div>
              ) : null}
            </>
          )}
        </div>

        <div className="flex flex-wrap gap-2 border-t border-border px-4 py-3 sm:px-5">
          <Button variant="outline" className="rounded-md flex-1" onClick={onClose}>
            Close
          </Button>
          {canWhatsapp && (
            <Button
              className="rounded-md flex-1"
              loading={mutation.isPending}
              onClick={buildDraft}
              icon={MessageCircle}
            >
              {draft ? 'Refresh draft' : 'Build draft'}
            </Button>
          )}
          {draft?.wa_me_url ? (
            <a
              href={draft.wa_me_url}
              target="_blank"
              rel="noreferrer"
              className="inline-flex flex-1 items-center justify-center gap-1.5 rounded-md bg-ink px-3 py-2 text-xs font-medium text-ink-on-dark hover:bg-neutral-800"
            >
              <ExternalLink className="h-3.5 w-3.5" />
              Open WhatsApp
            </a>
          ) : null}
        </div>
      </div>
    </ModalShell>
  );
};

export default WhatsAppDraftModal;
